import { Drawer, Box, Typography, Dialog, DialogContent, Stack, Avatar, Card, DialogTitle, CardContent, IconButton, TableContainer, Paper, Table, TableBody, TableRow, TableCell, Badge, Button, LinearProgress, Chip } from '@mui/material';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import VideocamIcon from '@mui/icons-material/Videocam';
import ForumIcon from '@mui/icons-material/Forum';
import useFirebase from "hooks/useFirebase";
import useApi from "hooks/useApi";
import ChatList from './ChatList';
import { ChatThread, ChatParticipant } from 'types/chat';
import StartChatPanel from 'components/chat/StartChatPanel';
import { useChatNotificationContext } from 'contexts/ChatNotificationContext';

// Type for unified communication items
interface CommunicationItem {
    type: 'call' | 'recording' | 'live_chat';
    timestamp: Date;
    originalData: any;
    id: string;
}

interface MissedCallDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    history?: any[];
    formatToLocalTime: (date: string) => string;
    video: any;
    handlePlayVideo: (url: string) => void;
    handleImage: (image: string, index: number) => void;
    transactionId?: string;
    transactionKey?: string;
    /**
     * Quick Chat: uids of the users on this transaction — assigned via a step's
     * RESOURCE field, or having submitted a step. Forwarded to StartChatPanel so
     * "Start New Live Chat" opens pre-filtered to them instead of the whole team,
     * mirroring what Quick Call does for the call dialog. Omit (or pass empty) to
     * keep the full-team roster.
     */
    assignedUserUids?: string[];
}

// `url` is the PRESIGNED link the backend generates on read
// (filesService.getSignedUrl(file_path) — the same treatment screenshots and
// every other file get). `public_url` is the raw S3 object built at write time;
// Agora uploads these private, and the bucket's website endpoint only serves
// public objects, so hitting it directly returns 403. Prefer the signed one.
const recordingUrl = (rec: any): string => rec?.url || rec?.public_url || '';

const MissedCallDetailsDrawer = ({
    open,
    onClose,
    history,
    formatToLocalTime,
    video,
    handlePlayVideo,
    handleImage,
    transactionId,
    transactionKey,
    assignedUserUids
}: MissedCallDetailsDrawerProps) => {
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoData, setInfoData] = useState<any>(null);
    const [chatList, setChatList] = useState<any>([]);
    
    // Filter state - 'all' is default
    const [activeFilter, setActiveFilter] = useState<'all' | 'recording' | 'call' | 'live_chat'>('all');
    
    // Live Chat state
    const [threads, setThreads] = useState<ChatThread[]>([]);
    const [loadingThreads, setLoadingThreads] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [markingAllRead, setMarkingAllRead] = useState(false);
    const [currentView, setCurrentView] = useState<'history' | 'newChat'>('history');
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const { getMessageHistory } = useFirebase();
    const { getChatThreadsByTransaction, markAllChatsAsRead, showToast } = useApi();
    const { 
        pendingChatNotification, 
        clearPendingChatNotification,
        openChatWindow
    } = useChatNotificationContext();

    // Load chat threads when drawer opens
    const loadThreads = useCallback(async (silent = false) => {
        if (!transactionId) return;
        
        if (silent) {
            setIsRefreshing(true);
        } else {
            setLoadingThreads(true);
        }
        try {
            const response = await getChatThreadsByTransaction(transactionId);
            if (response.success) {
                // Show all threads for this transaction (no participant filtering)
                setThreads(response.data || []);
                return response.data || [];
            }
        } catch (error) {
            console.error('Error loading chat threads:', error);
        } finally {
            setLoadingThreads(false);
            setIsRefreshing(false);
        }
        return [];
    }, [transactionId, getChatThreadsByTransaction]);

    useEffect(() => {
        if (open && transactionId) {
            loadThreads();
            setCurrentView('history'); // Reset view when drawer opens
        }
        
        // Cleanup when drawer closes to free memory
        if (!open) {
            setThreads([]);
            setChatList([]);
            setInfoData(null);
        }
    }, [open, transactionId, loadThreads]);

    // Auto-refresh threads every 6 seconds when drawer is open (reduced frequency)
    useEffect(() => {
        if (open && transactionId) {
            refreshIntervalRef.current = setInterval(() => {
                loadThreads(true); // Silent refresh
            }, 6000);
        }
        
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [open, transactionId, loadThreads]);

    // Handle pending chat notification - auto-open chat when navigated from notification
    useEffect(() => {
        if (open && pendingChatNotification?.threadUid && threads.length > 0) {
            const targetThread = threads.find(t => t.uid === pendingChatNotification.threadUid);
            if (targetThread) {
                // Open chat window using global context
                openChatWindow(targetThread, transactionKey);
                clearPendingChatNotification();
            }
        }
    }, [open, pendingChatNotification, threads, clearPendingChatNotification, openChatWindow, transactionKey]);

    // Create unified communication list combining call history and chat threads
    const unifiedCommunicationList = useMemo((): CommunicationItem[] => {
        const items: CommunicationItem[] = [];

        // Add call/video history items
        if (history && history.length > 0) {
            history.forEach((item: any, idx: number) => {
                // Skip FORM events
                if (item.eventType === "FORM") return;

                // Missed calls
                if ((item?.eventStatus === "MISSED_CALL" || item?.eventStatus === "MANUAL_DISCONNECT") && item?.eventStatus !== "COMPLETED") {
                    items.push({
                        type: 'call',
                        timestamp: new Date(item?.updatedAt || item?.createdAt),
                        originalData: item,
                        id: `call-${idx}-${item?.updatedAt}`
                    });
                }
                // Completed video sessions and chat sessions - both are "recordings"
                else if ((item?.eventStatus === "COMPLETED" || item?.eventStatus === "RECEIVED") && item?.videoSession) {
                    items.push({
                        type: 'recording',
                        timestamp: new Date(item?.updatedAt || item?.createdAt),
                        originalData: item,
                        id: `recording-${idx}-${item?.updatedAt}`
                    });
                }
            });
        }

        // Add live chat threads
        threads.forEach((thread) => {
            items.push({
                type: 'live_chat',
                timestamp: new Date(thread.lastMessageAt || thread.createdAt),
                originalData: thread,
                id: `live-chat-${thread.uid}`
            });
        });

        // Sort by timestamp descending (newest first)
        items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        return items;
    }, [history, threads]);

    // Filtered communication list based on active filter
    const filteredCommunicationList = useMemo(() => {
        if (activeFilter === 'all') {
            return unifiedCommunicationList;
        }
        return unifiedCommunicationList.filter(item => item.type === activeFilter);
    }, [unifiedCommunicationList, activeFilter]);

    // Live Chat handlers
    const handleStartChat = () => {
        setCurrentView('newChat');
    };

    const handleChatCreated = (newThread: ChatThread) => {
        setThreads(prev => [newThread, ...prev]);
        setCurrentView('history');
        // Open chat window using global context
        openChatWindow(newThread, transactionKey);
    };

    const handleOpenThread = (thread: ChatThread) => {
        // Open chat window using global context
        openChatWindow(thread, transactionKey);
    };

    const handleMarkAllRead = async () => {
        const unreadThreads = threads.filter((thread) => (thread.unreadCount || 0) > 0);
        if (unreadThreads.length === 0 || markingAllRead) return;

        const previousThreads = threads;

        setMarkingAllRead(true);
        setThreads((prev) => prev.map((thread) => ({ ...thread, unreadCount: 0 })));
        try {
            const response = await markAllChatsAsRead();
            const messagesMarked = response?.data?.messagesMarkedAsRead ?? 0;
            showToast(
                messagesMarked > 0 ? `Marked ${messagesMarked} messages as read.` : 'All accessible messages marked as read.',
                'success'
            );

            loadThreads(true);
        } catch {
            setThreads(previousThreads);
            showToast('Could not mark messages as read.', 'error');
        } finally {
            setMarkingAllRead(false);
        }
    };

    const unreadThreadsCount = threads.filter((thread) => (thread.unreadCount || 0) > 0).length;

    const formatChatTime = (dateString: string | Date) => {
        const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        
        // If today, show "Today, time"
        if (dateOnly.getTime() === today.getTime()) {
            return `Today, ${timeStr}`;
        }
        // If yesterday, show "Yesterday, time"
        else if (dateOnly.getTime() === yesterday.getTime()) {
            return `Yesterday, ${timeStr}`;
        }
        // If within last 7 days, show "weekday, time"
        else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
            const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
            return `${weekday}, ${timeStr}`;
        }
        // Otherwise show "date, time"
        else {
            const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `${dateStr}, ${timeStr}`;
        }
    };

    const getParticipantNames = (participants: ChatParticipant[]) => {
        if (!participants || participants.length === 0) return 'No participants';
        
        const names = participants.slice(0, 3).map(p => 
            typeof p === 'string' ? p : `${p.firstName || ''} ${p.lastName || ''}`.trim()
        );
        
        if (participants.length > 3) {
            return `${names.join(', ')} +${participants.length - 3}`;
        }
        return names.join(', ');
    };

    const handleInfoClick = async (item: any) => {
        setInfoData(item);
        setInfoOpen(true);
        if (item?.sessionType == "CHAT") {
            const viewMessage = await getMessageHistory(item?.videoSession?.sessionId);
            setChatList(viewMessage);
        }
    };

    const handleInfoClose = () => {
        setInfoOpen(false);
        setInfoData(null);
        setChatList([]);
    };

    function formatDateToSeconds(dateString: string) {
        const date = new Date(dateString);
        return date
            .toLocaleString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            })
            .replace(',', '');
    }

    // Render individual communication items
    const renderCommunicationItem = (commItem: CommunicationItem, idx: number) => {
        switch (commItem.type) {
            case 'call':
                return renderMissedCallCard(commItem.originalData, idx);
            case 'recording':
                return renderRecordingCard(commItem.originalData, idx);
            case 'live_chat':
                return renderLiveChatCard(commItem.originalData, idx);
            default:
                return null;
        }
    };

    // Missed call card
    const renderMissedCallCard = (item: any, idx: number) => (
        <Card
            key={`missed-${idx}`}
            elevation={1}
            sx={{
                mb: 1,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #fff1f0 0%, #ffffff 100%)',
                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.08)',
                border: "1px solid #ffe0e0",
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: '0 4px 16px rgba(244, 67, 54, 0.16)',
                    transform: 'translateY(-1px)'
                },
            }}
            onClick={() => handleInfoClick(item)}
        >
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                        sx={{
                            bgcolor: "error.main",
                            width: 32,
                            height: 32,
                        }}
                    >
                        <PhoneMissedIcon sx={{ color: "#fff", fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={{ fontWeight: 600, color: 'error.dark', fontSize: "13px", textTransform: "capitalize" }} noWrap>
                                {item?.updatedBy?.firstName} {item?.updatedBy?.lastName}
                            </Typography>
                            <Chip 
                                label="Missed Call" 
                                size="small" 
                                sx={{ 
                                    bgcolor: 'error.50', 
                                    color: 'error.main', 
                                    fontSize: 10,
                                    height: 20
                                }} 
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatToLocalTime(item?.updatedAt)}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    // Recording card (combines video calls and chat sessions)
    const renderRecordingCard = (item: any, idx: number) => {
        const isVideoCall = item?.sessionType !== "CHAT";
        const hasRecording = item?.videoSession?.recordings?.length > 0;
        
        return (
            <Card
                key={`recording-${idx}`}
                elevation={1}
                sx={{
                    mb: 1,
                    borderRadius: 2,
                    background: isVideoCall 
                        ? 'linear-gradient(90deg, #e3f2fd 0%, #ffffff 100%)'
                        : 'linear-gradient(90deg, #fffde7 0%, #ffffff 100%)',
                    boxShadow: isVideoCall 
                        ? '0 2px 8px rgba(33, 150, 243, 0.08)'
                        : '0 2px 8px rgba(255, 193, 7, 0.08)',
                    border: isVideoCall 
                        ? "1px solid rgba(33, 150, 243, 0.2)"
                        : "1px solid #ffe082",
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                        boxShadow: isVideoCall 
                            ? '0 4px 16px rgba(33, 150, 243, 0.16)'
                            : '0 4px 16px rgba(255, 193, 7, 0.16)',
                        transform: 'translateY(-1px)'
                    },
                }}
                onClick={() => handleInfoClick(item)}
            >
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                            sx={{
                                bgcolor: isVideoCall ? "primary.main" : "#ffb300",
                                width: 32,
                                height: 32,
                            }}
                        >
                            {isVideoCall 
                                ? <VideocamIcon sx={{ color: "#fff", fontSize: 18 }} />
                                : <ChatBubbleOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                            }
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography 
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: isVideoCall ? 'primary.dark' : '#ff9800', 
                                        fontSize: "13px", 
                                        textTransform: "capitalize" 
                                    }} 
                                    noWrap
                                >
                                    {item.videoSession?.requestedUserName}
                                    <span style={{ color: isVideoCall ? '#90caf9' : '#ffb300', margin: '0 4px' }}>→</span>
                                    {item.videoSession?.acceptedUserName}
                                </Typography>
                                <Chip 
                                    label={isVideoCall ? "Video Recording" : "Chat Recording"} 
                                    size="small" 
                                    sx={{ 
                                        bgcolor: isVideoCall ? 'primary.50' : '#fff8e1', 
                                        color: isVideoCall ? 'primary.main' : '#ff9800', 
                                        fontSize: 10,
                                        height: 20
                                    }} 
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {formatToLocalTime(item?.updatedAt)}
                            </Typography>
                        </Box>
                        {hasRecording && (
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // The backend stores the S3 link as `public_url`
                                    // (see VideoSessions.recordings) — `url` has never
                                    // existed, so Play always opened `undefined` and a
                                    // finished recording looked like it went nowhere.
                                    handlePlayVideo(recordingUrl(item.videoSession.recordings[0]));
                                }}
                            >
                                <img
                                    src={video}
                                    alt="Play"
                                    style={{ width: 28, height: 28, objectFit: 'contain' }}
                                />
                            </IconButton>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    // Live chat thread card (consistent with other cards)
    const renderLiveChatCard = (thread: ChatThread, idx: number) => (
        <Card
            key={`live-chat-${thread.uid}`}
            elevation={1}
            sx={{
                mb: 1,
                borderRadius: 2,
                background: thread.unreadCount 
                    ? 'linear-gradient(90deg, #e8f5e9 0%, #ffffff 100%)'
                    : 'linear-gradient(90deg, #f5f5f5 0%, #ffffff 100%)',
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.08)',
                border: '1px solid',
                borderColor: thread.unreadCount ? 'rgba(76, 175, 80, 0.3)' : 'grey.200',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: '0 4px 16px rgba(76, 175, 80, 0.16)',
                    transform: 'translateY(-1px)'
                },
            }}
            onClick={() => handleOpenThread(thread)}
        >
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Badge
                        badgeContent={thread.unreadCount || 0}
                        color="error"
                        overlap="circular"
                    >
                        <Avatar
                            sx={{
                                bgcolor: "#4caf50",
                                width: 32,
                                height: 32,
                            }}
                        >
                            {thread.participants?.length > 2 ? (
                                <GroupIcon sx={{ color: "#fff", fontSize: 18 }} />
                            ) : (
                                <ForumIcon sx={{ color: "#fff", fontSize: 18 }} />
                            )}
                        </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography 
                                sx={{ 
                                    fontWeight: thread.unreadCount ? 700 : 600, 
                                    color: '#2e7d32', 
                                    fontSize: "13px"
                                }} 
                                noWrap
                            >
                                {thread.subject || 'Live Chat'}
                            </Typography>
                            <Chip 
                                label="Live Chat" 
                                size="small" 
                                sx={{ 
                                    bgcolor: '#e8f5e9', 
                                    color: '#4caf50', 
                                    fontSize: 10,
                                    height: 20
                                }} 
                            />
                        </Stack>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                                display: 'block',
                                mt: 0.5,
                                fontWeight: thread.unreadCount ? 500 : 400
                            }}
                            noWrap
                        >
                            {thread.lastMessage?.message || 'No messages yet'}
                        </Typography>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    noWrap
                                    sx={{ maxWidth: 150 }}
                                >
                                    {getParticipantNames(thread.participants)}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {thread.lastMessageAt ? formatChatTime(thread.lastMessageAt) : formatChatTime(thread.createdAt)}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <>
        <Drawer 
            anchor="right" 
            open={open} 
            onClose={onClose}
            variant="persistent"
        >
            <Box sx={{ width: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {currentView === 'newChat' ? (
                    /* New Chat Panel - Facebook Messenger Style */
                    <StartChatPanel
                        onBack={() => setCurrentView('history')}
                        transactionId={transactionId || ''}
                        transactionKey={transactionKey}
                        onChatCreated={handleChatCreated}
                        restrictToUids={assignedUserUids}
                    />
                ) : (
                    /* Main History View */
                    <>
                        {/* Header */}
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'primary.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ChatBubbleOutlineIcon />
                                <Typography variant="h6" fontWeight={600}>
                                    Communication History
                                </Typography>
                            </Box>
                            <IconButton onClick={onClose} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Transaction Info */}
                        {/* {transactionKey && (
                            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Transaction
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {transactionKey}
                                </Typography>
                            </Box>
                        )} */}

                        {/* Slim nav loader for refreshing */}
                        <Box sx={{ height: 2 }}>
                            {(loadingThreads || isRefreshing) && (
                                <LinearProgress 
                                    sx={{ 
                                        height: 2,
                                        '& .MuiLinearProgress-bar': {
                                            transition: 'none'
                                        }
                                    }} 
                                />
                            )}
                        </Box>

                        {/* Filter Chips - Fixed at top */}
                        <Box sx={{ 
                            px: 1.5, 
                            py: 1, 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.75, 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            bgcolor: 'grey.50'
                        }}>
                            <Chip 
                                label="All" 
                                size="small" 
                                variant={activeFilter === 'all' ? 'filled' : 'outlined'}
                                onClick={() => setActiveFilter('all')}
                                sx={{ 
                                    fontSize: 10, 
                                    height: 24, 
                                    cursor: 'pointer',
                                    bgcolor: activeFilter === 'all' ? 'primary.main' : undefined,
                                    color: activeFilter === 'all' ? 'white' : undefined,
                                    '&:hover': { 
                                        bgcolor: activeFilter === 'all' ? 'primary.dark' : 'grey.200' 
                                    }
                                }} 
                            />
                            <Chip 
                                icon={<VideocamIcon sx={{ fontSize: 14 }} />}
                                label="Recordings" 
                                size="small" 
                                variant={activeFilter === 'recording' ? 'filled' : 'outlined'}
                                onClick={() => setActiveFilter('recording')}
                                sx={{ 
                                    fontSize: 10, 
                                    height: 24, 
                                    cursor: 'pointer',
                                    bgcolor: activeFilter === 'recording' ? 'primary.main' : undefined,
                                    color: activeFilter === 'recording' ? 'white' : undefined,
                                    '& .MuiChip-icon': { color: activeFilter === 'recording' ? 'white' : 'primary.main' },
                                    '&:hover': { 
                                        bgcolor: activeFilter === 'recording' ? 'primary.dark' : 'grey.200' 
                                    }
                                }} 
                            />
                            <Chip 
                                icon={<PhoneMissedIcon sx={{ fontSize: 14 }} />}
                                label="Missed Calls" 
                                size="small" 
                                variant={activeFilter === 'call' ? 'filled' : 'outlined'}
                                onClick={() => setActiveFilter('call')}
                                sx={{ 
                                    fontSize: 10, 
                                    height: 24, 
                                    cursor: 'pointer',
                                    bgcolor: activeFilter === 'call' ? 'error.main' : undefined,
                                    color: activeFilter === 'call' ? 'white' : undefined,
                                    '& .MuiChip-icon': { color: activeFilter === 'call' ? 'white' : 'error.main' },
                                    '&:hover': { 
                                        bgcolor: activeFilter === 'call' ? 'error.dark' : 'grey.200' 
                                    }
                                }} 
                            />
                            <Chip 
                                icon={<ForumIcon sx={{ fontSize: 14 }} />}
                                label="Live Chat" 
                                size="small" 
                                variant={activeFilter === 'live_chat' ? 'filled' : 'outlined'}
                                onClick={() => setActiveFilter('live_chat')}
                                sx={{ 
                                    fontSize: 10, 
                                    height: 24, 
                                    cursor: 'pointer',
                                    bgcolor: activeFilter === 'live_chat' ? '#4caf50' : undefined,
                                    color: activeFilter === 'live_chat' ? 'white' : undefined,
                                    '& .MuiChip-icon': { color: activeFilter === 'live_chat' ? 'white' : '#4caf50' },
                                    '&:hover': { 
                                        bgcolor: activeFilter === 'live_chat' ? '#388e3c' : 'grey.200' 
                                    }
                                }} 
                            />
                        </Box>

                        {unreadThreadsCount > 0 && (
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {unreadThreadsCount} unread live chat{unreadThreadsCount === 1 ? '' : 's'}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleMarkAllRead}
                                    sx={{ textTransform: 'none', minWidth: 110 }}
                                >
                                    {markingAllRead ? 'Marking...' : 'Mark all read'}
                                </Button>
                            </Box>
                        )}

                        {/* Unified Communication List */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                            {filteredCommunicationList.length === 0 && !loadingThreads ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 300,
                                        p: 3,
                                        textAlign: 'center'
                                    }}
                                >
                                    <ChatBubbleOutlineIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                                    <Typography color="text.secondary" gutterBottom>
                                        {activeFilter === 'all' 
                                            ? 'No communication history yet' 
                                            : `No ${activeFilter === 'call' ? 'missed calls' : activeFilter === 'recording' ? 'recordings' : 'live chats'} found`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {activeFilter === 'all' 
                                            ? 'Start a live chat or make calls to see them here'
                                            : 'Try selecting a different filter'}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Communication Items */}
                                    {filteredCommunicationList.map((item, idx) => renderCommunicationItem(item, idx))}
                                </>
                            )}
                        </Box>

                        {/* Start New Chat Button - Fixed at Bottom */}
                        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<AddCommentIcon />}
                                onClick={handleStartChat}
                                sx={{
                                    py: 1.25,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 2
                                }}
                            >
                                Start New Live Chat
                            </Button>
                        </Box>
                    </>
                )}
            </Box>

            <Dialog open={infoOpen} onClose={handleInfoClose} maxWidth="xs" fullWidth>
                {/* Communication Details */}
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, fontWeight: "700" }}>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: "700", marginRight: "5px" }}>Step:</Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: "500", marginRight: "5px" }}>{infoData?.step?.title}</Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleInfoClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            ml: 2,
                        }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {infoData && (
                        <Box>
                            {infoData?.videoSession && (
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                                    {infoData?.videoSession && infoData?.sessionType !== "CHAT" && (
                                        <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
                                            <Table>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ p: 0.8 }}><strong>Requested By</strong></TableCell>
                                                        <TableCell sx={{ textTransform: "capitalize", p: 1 }}>
                                                            {infoData.videoSession.requestedUserName} on{" "}
                                                            {formatDateToSeconds(infoData.videoSession.requestedAt)}
                                                        </TableCell>
                                                    </TableRow>

                                                    <TableRow>
                                                        <TableCell sx={{ p: 0.8 }}><strong>Accepted By</strong></TableCell>
                                                        <TableCell sx={{ textTransform: "capitalize", p: 1 }}>
                                                            {infoData.videoSession.acceptedUserName} on{" "}
                                                            {formatDateToSeconds(infoData.videoSession.acceptedAt)}
                                                        </TableCell>
                                                    </TableRow>

                                                    <TableRow>
                                                        <TableCell sx={{ p: 0.8 }}><strong>Terminated By</strong></TableCell>
                                                        <TableCell sx={{ p: 0.8 }}>{infoData.videoSession.terminatedUserName}</TableCell>
                                                    </TableRow>

                                                    {infoData.videoSession.userIds?.length > 0 && (
                                                        <TableRow>
                                                            <TableCell sx={{ p: 0.8 }}><strong>Participants</strong></TableCell>
                                                            <TableCell sx={{ p: 0.8 }}>
                                                                <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                                                                    {infoData.videoSession.userIds.map((u: any, idx: number) => (
                                                                        <li key={idx}>
                                                                            {u.firstName} {u.lastName}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    {infoData.videoSession.recordings?.length > 0 && (
                                                        <TableRow>
                                                            <TableCell sx={{ p: 0.8 }}><strong>Recordings</strong></TableCell>
                                                            <TableCell sx={{ p: 0.8 }}>
                                                                <Stack direction="row" spacing={1}>
                                                                    {infoData.videoSession.recordings.map((rec: any, idx: number) => (
                                                                        <IconButton
                                                                            key={idx}
                                                                            color="primary"
                                                                            onClick={() => handlePlayVideo(recordingUrl(rec))}
                                                                        >
                                                                            <img
                                                                                src={video}
                                                                                alt="Play"
                                                                                style={{ width: 32, height: 32, objectFit: "contain" }}
                                                                            />
                                                                        </IconButton>
                                                                    ))}
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    {infoData?.videoSession?.screenshots?.length > 0 && (
                                                        <TableRow>
                                                            <TableCell><strong>Screenshots</strong></TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                    {infoData.videoSession.screenshots
                                                                        ?.filter((file: any) => {
                                                                            const ext = file.file_name?.split(".").pop()?.toLowerCase() || "";
                                                                            return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
                                                                        })
                                                                        .map((file: any, fileIdx: number) => (
                                                                            <Box
                                                                                key={`screenshot-${fileIdx}-${file?._id || file?.url}`}
                                                                                sx={{
                                                                                    width: 50,
                                                                                    height: 50,
                                                                                    borderRadius: 1,
                                                                                    overflow: "hidden",
                                                                                    cursor: "pointer",
                                                                                    border: "1.5px solid",
                                                                                    borderColor: "grey.200",
                                                                                    transition: "all 0.2s ease-in-out",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                    bgcolor: "transparent",
                                                                                    "&:hover": {
                                                                                        borderColor: "primary.main",
                                                                                        transform: "scale(1.05)",
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <img
                                                                                    src={file.url}
                                                                                    alt={file.file_name}
                                                                                    style={{
                                                                                        width: "100%",
                                                                                        height: "100%",
                                                                                        objectFit: "cover",
                                                                                        display: "block",
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();

                                                                                        // collect all images
                                                                                        const fieldImages = infoData.videoSession.screenshots
                                                                                            .filter((f: any) => {
                                                                                                const fileExt = f.file_name?.split(".").pop()?.toLowerCase() || "";
                                                                                                return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(fileExt);
                                                                                            })
                                                                                            .map((f: any) => ({ src: f.url }));

                                                                                        // set clicked index
                                                                                        const imageIndex = fieldImages.findIndex((f: any) => f.src === file.url);
                                                                                        const indexData = imageIndex >= 0 ? imageIndex : 0;
                                                                                        handleImage(fieldImages, indexData);
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        ))}
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                            {chatList.length > 0 && <ChatList chatList={chatList} />}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Drawer>
        {/* Chat Windows are now rendered globally via ChatNotificationContext for persistence across navigation */}
        </>
    );
};

export default MissedCallDetailsDrawer;